import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { REF_TYPE_META, fmtDate, fmtDays } from "@/pages/hr/leave/leaveUtils";

const REF_TYPES = ["opening", "application", "cancellation", "adjustment"];

export default function LeaveLedgerTab({ employees = [], types = [] }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ employee_id: "", leave_type_id: "", reference_type: "" });

  const load = () => {
    const q = Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/hr/leave/ledger${q ? "?" + q : ""}`).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  return (
    <div data-testid="leave-ledger-tab">
      <div className="flex flex-wrap gap-2 mb-3 items-end rounded-lg border border-slate-200 bg-white p-3">
        <div><Label className="text-xs">Employee</Label>
          <Select value={f.employee_id || "all"} onValueChange={(v) => setF({ ...f, employee_id: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-48" data-testid="ledger-filter-employee"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All employees</SelectItem>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.full_name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Type</Label>
          <Select value={f.leave_type_id || "all"} onValueChange={(v) => setF({ ...f, leave_type_id: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-36" data-testid="ledger-filter-type"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.code}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Entry Type</Label>
          <Select value={f.reference_type || "all"} onValueChange={(v) => setF({ ...f, reference_type: v === "all" ? "" : v })}>
            <SelectTrigger className="h-8 w-44" data-testid="ledger-filter-reftype"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All entries</SelectItem>{REF_TYPES.map((r) => <SelectItem key={r} value={r}>{(REF_TYPE_META[r] || {}).label || r}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="excel-table w-full" data-testid="leave-ledger-table">
          <thead><tr><th>Date</th><th>Employee</th><th>Type</th><th>Entry</th><th>Effect</th><th>Source</th><th>Note</th><th>By</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={8} className="text-center text-slate-400 py-6">No ledger entries</td></tr>
              : rows.map((r) => {
                const m = REF_TYPE_META[r.reference_type] || { label: r.reference_type, cls: "text-slate-600 border-slate-300" };
                return (
                  <tr key={r.id} data-testid={`ledger-row-${r.id}`}>
                    <td className="text-xs font-mono">{fmtDate(r.date)}</td>
                    <td className="text-xs font-mono">{r.employee_id}</td>
                    <td className="text-xs">{r.leave_type_code}</td>
                    <td><Badge variant="outline" className={m.cls}>{m.label}</Badge></td>
                    <td className={`text-center font-semibold ${r.effect < 0 ? "text-rose-600" : "text-emerald-700"}`}>{r.effect > 0 ? "+" : ""}{fmtDays(r.effect)}</td>
                    <td className="text-xs">{r.source}</td>
                    <td className="text-xs max-w-[220px] truncate" title={r.note}>{r.note || "—"}</td>
                    <td className="text-xs text-slate-500">{r.created_by || "—"}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
